import React from "react";
import { connect } from "react-redux";

import { ILocation } from "../../../models/location.interfaces";
import { isZipLocation } from "../../../utils/guards";
import { preferredLocationSelector } from "../../common/selectors";
import { TDispatchMapper, TStateMapper } from "../../reducers.interfaces";
import { PredictedDeliveryDateStatus } from "../constants";
import { Dispatchers } from "../dispatchers";
import { Loaders } from "../loaders";
import { IDeliveryDatePredictionState } from "../reducers.interfaces";

const PREDICTION_TTL = 3600 * 1000; // 1 hour

interface IOwnProps {}

interface IReduxProps {
    location: ILocation | null;
    prediction: IDeliveryDatePredictionState;
}

interface IDispatchProps {
    loadPredictedDeliveryDate: Loaders["loadPredictedDeliveryDate"];
}

interface IProps extends IOwnProps, IReduxProps, IDispatchProps {}

interface IState {}

// TODO(ssr, LEVEL 5 out of 10): Migrate this component from class-style to functional-style. Rated LOE as 5/10 due to 117 LOC with lifecycle methods (componentDidMount, componentDidUpdate), complex conditional logic for determining when to load predictions, TTL-based caching (1 hour), location validation, Redux integration, and async data loading. Moderate complexity.
class PredictedDeliveryLoaderComponent extends React.Component<IProps, IState> {
    componentDidMount() {
        this.loadPredictedDeliveryDate();
    }

    componentDidUpdate() {
        this.loadPredictedDeliveryDate();
    }

    private loadPredictedDeliveryDate() {
        // If we don't have a ZIP code, we can't load a prediction.
        if (!this.props.location || !isZipLocation(this.props.location)) {
            // TODO(i18n): This is a developer-facing log message - no translation needed
            console.log(
                "Location is not suitable for delivery date prediction",
            );
            return;
        }
        // If the prediction is already loading, just wait for it.
        if (
            this.props.prediction.status === PredictedDeliveryDateStatus.LOADING
        ) {
            // TODO(i18n): This is a developer-facing log message - no translation needed
            console.log("Delivery date prediction is already loading");
            return;
        }
        // If we've already loaded data for this ZIP code and got back an
        // error, don't bother trying to load the data again.
        if (
            this.props.prediction.status ===
                PredictedDeliveryDateStatus.ERROR &&
            this.props.location.zip === this.props.prediction.data.postcode
        ) {
            // TODO(i18n): This is a developer-facing log message - no translation needed
            console.log("Delivery date prediction is already up to date");
            return;
        }
        // If we have a valid prediction, and it's not too old (1 hour TTL), don't
        // bother loading the data again.
        if (
            this.props.prediction.status ===
                PredictedDeliveryDateStatus.LOADED &&
            this.props.location.zip === this.props.prediction.data.postcode &&
            this.props.prediction.data.created_datetime
        ) {
            const createdTimestamp = new Date(
                this.props.prediction.data.created_datetime,
            ).getTime();
            const expiresOnTimestamp = createdTimestamp + PREDICTION_TTL;
            const nowTimestamp = new Date().getTime();
            if (expiresOnTimestamp > nowTimestamp) {
                // TODO(i18n): This is a developer-facing log message - no translation needed
                console.log("Delivery date prediction is already up to date");
                return;
            }
        }
        // We've passed all the gates, so fetch the prediction now.
        this.props.loadPredictedDeliveryDate(this.props.location.zip);
    }

    render() {
        return null;
    }
}

const mapStateToProps: TStateMapper<"checkout", IReduxProps, IOwnProps> = (
    rootState,
    ownProps,
) => {
    return {
        location: preferredLocationSelector(rootState),
        prediction: rootState.checkout.data.predicted_delivery_date,
        ...ownProps,
    };
};

const mapDispatchToProps: TDispatchMapper<IDispatchProps> = (dispatch) => {
    const dispatchers = new Dispatchers(dispatch);
    const loaders = new Loaders(dispatchers);
    return {
        loadPredictedDeliveryDate: loaders.loadPredictedDeliveryDate,
    };
};

export const PredictedDeliveryLoader = connect(
    mapStateToProps,
    mapDispatchToProps,
)(PredictedDeliveryLoaderComponent);
